/** @format */

import {
	Chain,
	ProductPrice,
	ProductStylePrices,
	SkuAttribute
} from '@shared/data-access/interfaces';
import { Component, DestroyRef, inject, input, OnDestroy, OnInit } from '@angular/core';
import { ProductSelectionService } from '@pdp/data-access/services';
import { combineLatest, Subject } from 'rxjs';
import { selectHasEmptyPriceAndHasPrice, selectResolvedSku } from '@pdp/data-access/state';
import { take, takeUntil } from 'rxjs/operators';

import { IntermediaryProductPrice } from '@pdp/data-access/interfaces';
import { KillswitchService, UtilityService } from '@shared/data-access/services';

import { Store } from '@ngrx/store';

import { oosPrice } from './price-constants';
import { selectSelectedColorAttribute } from '@pdp/attributes/data-access';
import { getPrice, getProductPriceFromSku } from '@pdp/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	selector: 'pdp-price',
	templateUrl: './price.component.html',
	styleUrl: './price.component.scss',
	standalone: false
})
export class PriceComponent implements OnInit, OnDestroy {
	readonly chain = inject(Chain);
	private readonly productSelectionService = inject(ProductSelectionService);
	private readonly store = inject(Store);
	private readonly utilityService = inject(UtilityService);
	private readonly killSwitchService = inject(KillswitchService);
	private readonly destroyRef = inject(DestroyRef);

	productPrice: ProductPrice;

	readonly myCustomizerTotalPrice = input<number>();
	readonly index = input<number>();
	readonly selectedBucketIndex = input<number>();
	readonly bucket = input<SkuAttribute>();
	readonly bundleProductPrice = input<ProductStylePrices>();
	readonly bundleOverallPrice = input<ProductPrice>();
	readonly updatePriceBasedOnSelectedColor = input<boolean>(false);

	/**
	 * @description
	 * Boolean representing if current product meets see price in cart and price attached condition.
	 */
	isProductEmptyPricedAndPriced: boolean;

	isSkuResolved: boolean;

	ecodePriceData = this.productSelectionService.getProductStyleData().prices;

	resolvedSkuSubscription$ = this.store.select(selectResolvedSku);
	selectedColorAttribute$ = this.store.select(selectSelectedColorAttribute);
	UNSUBSCRIBE$: Subject<void> = new Subject();
	isEnhancedSalesPriceEnable = false;

	ngOnInit() {
		// Get Killswitches
		this.killSwitchService
			.getKillSwitchesEnvironment$()
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe((data) => {
				this.isEnhancedSalesPriceEnable = this.utilityService.checkKillSwitch(
					'isEnhancedSalesPriceEnable',
					data,
					this.chain.chainIdentifierAbbr
				);
			});

		// check for Bucket and set Bucket Price
		if (this.bucket()) {
			this.productPrice = this.setBucketProductPriceData(this.bucket().productPrice);
		} else if (this.bundleProductPrice()) {
			this.productPrice = this.setEcodeProductPriceData(this.bundleProductPrice());
		} else if (this.bundleOverallPrice()) {
			this.productPrice = this.bundleOverallPrice();
		} else {
			this.productPrice = this.ecodePriceData
				? this.setEcodeProductPriceData(this.ecodePriceData)
				: oosPrice;
		}

		this.getIsProductEmptyPricedAndPriced();
		this.getResolvedSkuData();
	}

	convertPricesObjectValuesFromStringToInt(
		ecodePriceData: ProductStylePrices
	): IntermediaryProductPrice {
		const ecodePriceDataConvertedToInt = {};
		for (const key in ecodePriceData) {
			// eslint-disable-next-line no-prototype-builtins
			if (ecodePriceData.hasOwnProperty(key)) {
				ecodePriceDataConvertedToInt[key] = Math.abs(ecodePriceData[key]);
			}
		}
		return ecodePriceDataConvertedToInt as IntermediaryProductPrice;
	}

	getResolvedSkuData() {
		combineLatest([this.resolvedSkuSubscription$, this.selectedColorAttribute$])
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(([resolvedSkuData, selectedColor]) => {
				if (resolvedSkuData) {
					this.isSkuResolved = true;
					if (this.index() === this.selectedBucketIndex()) {
						this.productPrice = getProductPriceFromSku(resolvedSkuData.prices);
					} else {
						this.productPrice = this.setBucketProductPriceData(
							this.bucket().productPrice
						);
					}
				} else if (selectedColor && this.updatePriceBasedOnSelectedColor()) {
					this.productPrice = selectedColor.prices;
				} else {
					this.isSkuResolved = false;
					if (this.bucket()) {
						this.productPrice = this.setBucketProductPriceData(
							this.bucket().productPrice
						);
					} else if (this.bundleProductPrice()) {
						this.productPrice = this.setEcodeProductPriceData(
							this.bundleProductPrice()
						);
					} else if (this.bundleOverallPrice()) {
						this.productPrice = this.bundleOverallPrice();
					} else {
						this.productPrice = this.ecodePriceData
							? this.setEcodeProductPriceData(this.ecodePriceData)
							: oosPrice;
					}
				}
			});
	}

	getIsProductEmptyPricedAndPriced() {
		this.store
			.select(selectHasEmptyPriceAndHasPrice)
			.pipe(take(1))
			.subscribe((isPricedAndEmptyPriced) => {
				this.isProductEmptyPricedAndPriced = isPricedAndEmptyPriced;
			});
	}

	setBucketProductPriceData(price: ProductPrice) {
		const convertedPrice: IntermediaryProductPrice = {
			mapPriceIndicator: price.mapPriceIndicator,
			maxlistprice: Number(price.maxListPrice),
			maxofferprice: Number(price.maxOfferPrice),
			minlistprice: Number(price.minListPrice),
			minofferprice: Number(price.minOfferPrice),
			dealsPercentage: null,
			priceIndicator: price.priceIndicator
		};
		return getPrice(convertedPrice, convertedPrice.mapPriceIndicator === 1);
	}

	setEcodeProductPriceData(priceData: ProductStylePrices) {
		return getPrice(
			this.convertPricesObjectValuesFromStringToInt(priceData),
			priceData.mapPriceIndicator === 1
		);
	}

	/**
	 * @description Checks if select options text should be shown based on if product has 'see price in cart' and price tag attached to it.
	 */
	public showSelectOptionsText(): boolean {
		return this.isProductEmptyPricedAndPriced && !this.isSkuResolved && !this.bucket();
	}

	ngOnDestroy() {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}
}
